import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';

const HEADER_FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sansation:wght@400;700&display=swap');
`;

const RoleBasedHeader = ({ userRole = 'user', uploadProgress = null, pendingAudits = 0 }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen]     = useState(false);
  const [isDark, setIsDark] = useState(
    () => document.documentElement.getAttribute('data-theme') === 'dark'
  );
  const [toastMsg, setToastMsg] = useState(null);

  const location    = useLocation();
  const navigate    = useNavigate();
  const { user, userProfile, signOut } = useAuth();
  const userMenuRef = useRef(null);

  // Close mobile menu on route change
  useEffect(() => { setIsMobileMenuOpen(false); }, [location?.pathname]);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const onResize = () => { if (window.innerWidth > 768) setIsMobileMenuOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef?.current && !userMenuRef.current.contains(e.target))
        setIsUserMenuOpen(false);
    };
    if (isUserMenuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isUserMenuOpen]);

  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3000);
  };

  const userNavItems = [
    { label: 'Dashboard', path: '/user-dashboard',       icon: 'LayoutDashboard' },
    { label: 'Report',    path: '/audit-results-screen', icon: 'FileText',
      badge: pendingAudits > 0 ? pendingAudits : null },
    { label: 'Resources', path: null, icon: 'BookOpen', comingSoon: true },
  ];

  const adminNavItems = [
    { label: 'Console',          path: '/admin-console',          icon: 'Settings' },
    { label: 'Audit Management', path: '/admin-audit-management', icon: 'ClipboardList',
      badge: pendingAudits > 0 ? pendingAudits : null },
  ];

  const navItems = userRole === 'admin' ? adminNavItems : userNavItems;
  const isActive = (path) => location?.pathname === path;

  const handleLogout = async () => {
    try { await signOut(); navigate('/login-screen'); }
    catch (e) { console.error('Logout error:', e); }
  };

  const getUserInitials = () => {
    if (userProfile?.full_name) {
      const names = userProfile.full_name.split(' ');
      return names.length > 1
        ? `${names[0].charAt(0)}${names[names.length - 1].charAt(0)}`.toUpperCase()
        : names[0].charAt(0).toUpperCase();
    }
    return user?.email?.charAt(0)?.toUpperCase() || 'U';
  };

  const toggleTheme = () => {
    const next = isDark ? 'light' : 'dark';
    setIsDark(!isDark);
    document.documentElement.setAttribute('data-theme', next);
    localStorage.setItem('theme', next);
  };

  return (
    <>
      <style>{HEADER_FONT_CSS}</style>
      <style>{`
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateX(-50%) translateY(-8px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes cs-pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(0,130,251,0.18); }
          50%       { box-shadow: 0 0 0 3px rgba(0,130,251,0.06); }
        }

        /* ── Nav tabs: hide on mobile ── */
        .rbh-nav-center {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 24px;
          align-items: stretch;
          height: 52px;
        }

        /* ── Hamburger: hide on desktop ── */
        .rbh-hamburger { display: none; }

        /* ── Avatar menu: always visible ── */
        .rbh-avatar-btn {
          width: 32px; height: 32px; border-radius: 50%;
          background: linear-gradient(135deg, #004AAD 0%, #0082FB 100%);
          color: #ffffff; font-size: 11px; font-weight: 700;
          display: flex; align-items: center; justify-content: center;
          border: none; cursor: pointer; flex-shrink: 0;
          letter-spacing: 0.02em; font-family: inherit;
        }

        /* ── Mobile overlay backdrop ── */
        .rbh-overlay {
          display: none;
          position: fixed; inset: 0; top: 52px;
          background: rgba(0,0,0,0.35);
          z-index: 98;
          animation: fadeOverlay 0.2s ease;
        }
        @keyframes fadeOverlay {
          from { opacity: 0; } to { opacity: 1; }
        }

        /* ── Mobile drawer ── */
        .rbh-mobile-drawer {
          position: fixed;
          top: 52px; left: 0; right: 0;
          background: #ffffff;
          border-bottom: 1px solid #e2e8f0;
          z-index: 99;
          max-height: calc(100vh - 52px);
          overflow-y: auto;
          box-shadow: 0 12px 32px rgba(0,0,0,0.12);
          animation: slideDown 0.22s cubic-bezier(0.25,0.1,0.25,1);
          font-family: inherit;
          -webkit-overflow-scrolling: touch;
        }

        @media (max-width: 768px) {
          .rbh-nav-center { display: none; }
          .rbh-hamburger  { display: flex; }
          .rbh-overlay    { display: block; }
        }

        /* Nav link hover */
        .rbh-nav-link:hover { color: #0f172a !important; }

        /* Mobile menu item hover */
        .rbh-mob-item:hover { background: #f8fafc; }
        .rbh-mob-item-danger:hover { background: #fef2f2; }

        /* User dropdown item hover */
        .rbh-dd-item:hover { background: #f1f5f9; }
        .rbh-dd-danger:hover { background: #fef2f2; }
      `}</style>

      {/* ══ TOAST ══ */}
      {toastMsg && (
        <div style={{
          position: 'fixed', top: 64, left: '50%',
          transform: 'translateX(-50%)', zIndex: 999,
          background: '#0f172a', color: '#fff',
          padding: '10px 20px', borderRadius: 100,
          fontSize: 13, fontWeight: 500,
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          display: 'flex', alignItems: 'center', gap: 8,
          animation: 'fadeInDown 0.2s ease',
          fontFamily: 'inherit', whiteSpace: 'nowrap',
          maxWidth: 'calc(100vw - 32px)',
        }}>
          ⏳ Resources are coming soon!
        </div>
      )}

      {/* ══ TOP NAV ══ */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 24px', height: 52, flexShrink: 0,
        background: '#ffffff', borderBottom: '1px solid #e2e8f0',
        backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        fontFamily: 'inherit',
      }}>

        {/* ── Logo ── */}
        <Link
          to={userRole === 'admin' ? '/admin-console' : '/user-dashboard'}
          style={{ textDecoration: 'none', flexShrink: 0, display: 'flex', alignItems: 'center', zIndex: 1, overflow: 'visible' }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, userSelect: 'none' }}>
            <img
              src="/assets/images/8.svg"
              alt="DecX AI icon"
              style={{ width: 22, height: 22, objectFit: 'contain', flexShrink: 0 }}
            />
            <img
              src="/assets/images/12.svg"
              alt="DecX AI logo"
              style={{ height: 110, width: 'auto', objectFit: 'contain', flexShrink: 0 }}
            />
          </span>
        </Link>

        {/* ── Center Nav Tabs (desktop only) ── */}
        <nav className="rbh-nav-center">
          {navItems.map((item) => {
            const active = isActive(item.path);

            if (item.comingSoon) {
              return (
                <button
                  key={item.label}
                  onClick={() => showToast('Resources are coming soon!')}
                  style={{
                    fontSize: 13, fontWeight: 500,
                    color: '#6b7280', opacity: 0.55,
                    display: 'flex', alignItems: 'center',
                    gap: 0, whiteSpace: 'nowrap',
                    padding: '14px 0',
                    background: 'none', border: 'none',
                    borderBottom: '2px solid transparent',
                    cursor: 'pointer', fontFamily: 'inherit',
                    position: 'relative',
                  }}
                >
                  {item.label}
                  <span style={{
                    position: 'absolute',
                    right: -2, top: 10,
                    fontSize: 7.5, fontWeight: 700,
                    color: '#0055CC',
                    letterSpacing: '0.03em',
                    lineHeight: 1, opacity: 1,
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap',
                    fontFamily: 'inherit',
                  }}>
                    Coming Soon
                  </span>
                </button>
              );
            }

            return (
              <Link
                key={item.path}
                to={item.path}
                className="rbh-nav-link"
                style={{
                  fontSize: 13, fontWeight: active ? 600 : 500,
                  color: active ? '#0f172a' : '#6b7280',
                  textDecoration: 'none', display: 'flex', alignItems: 'center',
                  gap: 6, whiteSpace: 'nowrap', padding: '14px 0',
                  borderBottom: `2px solid ${active ? '#0082FB' : 'transparent'}`,
                  transition: 'color 0.15s',
                  fontFamily: 'inherit',
                }}
              >
                {item.label}
                {item.badge && (
                  <span style={{
                    fontSize: 10, fontWeight: 700,
                    background: '#0082FB', color: '#ffffff',
                    borderRadius: 10, padding: '1px 6px', lineHeight: 1.4,
                    fontFamily: 'inherit',
                  }}>
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* ── Right: Avatar + Hamburger ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, zIndex: 1 }}>

          {/* Avatar + Dropdown */}
          <div style={{ position: 'relative' }} ref={userMenuRef}>
            <button
              className="rbh-avatar-btn"
              onClick={() => { setIsUserMenuOpen(!isUserMenuOpen); setIsMobileMenuOpen(false); }}
              aria-label="User menu"
            >
              {getUserInitials()}
            </button>

            {isUserMenuOpen && (
              <div style={{
                position: 'absolute', right: 0, top: 'calc(100% + 8px)',
                width: 230, background: '#ffffff', border: '1px solid #e2e8f0',
                borderRadius: 14, boxShadow: '0 10px 40px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.08)',
                zIndex: 200, overflow: 'hidden', fontFamily: 'inherit',
                animation: 'slideDown 0.18s cubic-bezier(0.25,0.1,0.25,1)',
              }}>
                {/* User info */}
                <div style={{ padding: '14px 16px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
                  <p style={{
                    fontFamily: "'Sansation', sans-serif",
                    fontSize: 13, fontWeight: 700, color: '#0f172a', margin: 0, lineHeight: 1.4,
                  }}>
                    {userProfile?.full_name || 'User'}
                  </p>
                  <p style={{ fontSize: 11, color: '#64748b', margin: '3px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'inherit' }}>
                    {user?.email}
                  </p>
                </div>
                <div style={{ padding: '6px 0' }}>
                  {[
                    { label: 'Profile',  icon: 'User',     path: '/profile-screen'  },
                    { label: 'Settings', icon: 'Settings', path: '/settings-screen' },
                  ].map((item) => (
                    <button key={item.label}
                      className="rbh-dd-item"
                      onClick={() => { setIsUserMenuOpen(false); navigate(item.path); }}
                      style={{
                        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                        padding: '10px 16px', fontSize: 13, fontWeight: 500,
                        color: '#0f172a', background: 'transparent', border: 'none',
                        cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                        transition: 'background 0.1s',
                      }}
                    >
                      <span style={{ width: 28, height: 28, borderRadius: 8, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Icon name={item.icon} size={14} color="#64748b" />
                      </span>
                      {item.label}
                    </button>
                  ))}
                  <div style={{ borderTop: '1px solid #f1f5f9', margin: '6px 0' }} />
                  <button
                    className="rbh-dd-danger"
                    onClick={() => { setIsUserMenuOpen(false); handleLogout(); }}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 16px', fontSize: 13, fontWeight: 500,
                      color: '#dc2626', background: 'transparent', border: 'none',
                      cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                      transition: 'background 0.1s',
                    }}
                  >
                    <span style={{ width: 28, height: 28, borderRadius: 8, background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon name="LogOut" size={14} color="#dc2626" />
                    </span>
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Hamburger — mobile only */}
          <button
            className="rbh-hamburger"
            onClick={() => { setIsMobileMenuOpen(!isMobileMenuOpen); setIsUserMenuOpen(false); }}
            style={{
              width: 32, height: 32, borderRadius: 8,
              border: '1px solid #e2e8f0', background: '#ffffff',
              alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#0f172a', flexShrink: 0,
              transition: 'background 0.15s, border-color 0.15s',
            }}
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
          >
            <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={18} />
          </button>
        </div>
      </header>

      {/* ══ MOBILE BACKDROP ══ */}
      {isMobileMenuOpen && (
        <div
          className="rbh-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ══ MOBILE DRAWER ══ */}
      {isMobileMenuOpen && (
        <div className="rbh-mobile-drawer" role="navigation" aria-label="Mobile menu">

          {/* Nav items */}
          <div style={{ padding: '8px 0 4px' }}>
            {navItems.map((item) => {
              if (item.comingSoon) {
                return (
                  <div key={item.label}
                    className="rbh-mob-item"
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '13px 24px', cursor: 'default',
                      transition: 'background 0.1s',
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <span style={{
                        width: 32, height: 32, borderRadius: 9, flexShrink: 0,
                        background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Icon name={item.icon} size={16} color="#cbd5e1" />
                      </span>
                      <span style={{ fontSize: 14, fontWeight: 500, color: '#b0bac9', fontFamily: 'inherit' }}>{item.label}</span>
                    </span>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 3,
                      padding: '3px 9px', borderRadius: 100,
                      border: '1px solid rgba(0,130,251,0.22)',
                      background: 'rgba(0,130,251,0.06)',
                      fontSize: 9, fontWeight: 700,
                      letterSpacing: '0.08em', textTransform: 'uppercase',
                      color: '#0069d9', whiteSpace: 'nowrap',
                      fontFamily: 'inherit',
                    }}>
                      <svg width="6" height="6" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/>
                      </svg>
                      Soon
                    </span>
                  </div>
                );
              }

              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '13px 24px', fontSize: 14,
                    fontWeight: active ? 600 : 500,
                    color: active ? '#0082FB' : '#0f172a',
                    textDecoration: 'none', fontFamily: 'inherit',
                    background: active ? 'rgba(0,130,251,0.06)' : 'transparent',
                    transition: 'background 0.1s',
                    borderLeft: `3px solid ${active ? '#0082FB' : 'transparent'}`,
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{
                      width: 32, height: 32, borderRadius: 9, flexShrink: 0,
                      background: active ? 'rgba(0,130,251,0.10)' : '#f1f5f9',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Icon name={item.icon} size={16} color={active ? '#0082FB' : '#64748b'} />
                    </span>
                    {item.label}
                  </span>
                  {item.badge && (
                    <span style={{ fontSize: 10, fontWeight: 700, background: '#0082FB', color: '#ffffff', borderRadius: 10, padding: '2px 8px', fontFamily: 'inherit' }}>
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>

          {/* Divider + User info */}
          <div style={{ borderTop: '1px solid #f1f5f9', margin: '4px 0 0', padding: '14px 24px 10px', background: '#f8fafc' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                background: 'linear-gradient(135deg, #004AAD 0%, #0082FB 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
              }}>
                {getUserInitials()}
              </div>
              <div style={{ overflow: 'hidden' }}>
                <p style={{
                  fontFamily: "'Sansation', sans-serif",
                  fontSize: 13, fontWeight: 600, color: '#0f172a', margin: 0,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {userProfile?.full_name || 'User'}
                </p>
                <p style={{ fontSize: 11, color: '#64748b', margin: 0, fontFamily: 'inherit', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {user?.email}
                </p>
              </div>
            </div>
          </div>

          {/* Profile / Settings / Logout */}
          <div style={{ padding: '4px 0 16px' }}>
            {[
              { label: 'Profile',  icon: 'User',     path: '/profile-screen'  },
              { label: 'Settings', icon: 'Settings', path: '/settings-screen' },
            ].map((item) => (
              <button key={item.label}
                className="rbh-mob-item"
                onClick={() => { setIsMobileMenuOpen(false); navigate(item.path); }}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 24px', fontSize: 14, fontWeight: 500,
                  color: '#0f172a', background: 'transparent', border: 'none',
                  cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                  transition: 'background 0.1s',
                }}
              >
                <span style={{ width: 32, height: 32, borderRadius: 9, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon name={item.icon} size={16} color="#64748b" />
                </span>
                {item.label}
              </button>
            ))}

            <div style={{ borderTop: '1px solid #f1f5f9', margin: '6px 16px' }} />

            <button
              className="rbh-mob-item-danger"
              onClick={() => { setIsMobileMenuOpen(false); handleLogout(); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 24px', fontSize: 14, fontWeight: 500,
                color: '#dc2626', background: 'transparent', border: 'none',
                cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                transition: 'background 0.1s',
              }}
            >
              <span style={{ width: 32, height: 32, borderRadius: 9, background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon name="LogOut" size={16} color="#dc2626" />
              </span>
              Logout
            </button>
          </div>
        </div>
      )}

      {uploadProgress !== null && <UploadProgressIndicator progress={uploadProgress} />}
    </>
  );
};

/* ══ UPLOAD PROGRESS INDICATOR ══ */
const UploadProgressIndicator = ({ progress }) => {
  const estimatedTime = Math.max(0, Math.ceil((100 - progress) / 10));
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24,
      background: '#ffffff', border: '1px solid #e2e8f0',
      borderRadius: 12, padding: '12px 16px',
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
      zIndex: 200, display: 'flex', alignItems: 'center',
      gap: 12, minWidth: 220,
      maxWidth: 'calc(100vw - 48px)',
      fontFamily: 'inherit',
    }}>
      <Icon name="Upload" size={20} color="#0082FB" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ height: 4, background: '#f1f5f9', borderRadius: 2, overflow: 'hidden', marginBottom: 4 }}>
          <div style={{
            width: `${progress}%`, height: '100%',
            background: 'linear-gradient(90deg, #004AAD, #0082FB)',
            borderRadius: 2, transition: 'width 0.3s ease',
          }} />
        </div>
        <span style={{ fontSize: 11, color: '#64748b', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
          {progress}% {estimatedTime > 0 ? `· ${estimatedTime}s remaining` : '· Complete'}
        </span>
      </div>
    </div>
  );
};

export default RoleBasedHeader;